import dayjs from 'dayjs';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Box,
    Button,
    CircularProgress,
    DialogActions,
    Divider,
    FormHelperText,
    FormLabel,
    Grid,
    InputAdornment,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Chip,
    alpha,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
    BarChart,
    CurrencyRupee,
    CalendarMonthOutlined,
    ReceiptLongOutlined,
    CloudUploadOutlined,
    DescriptionOutlined,
    NotesOutlined,
    MedicalServicesOutlined,
    AccountBalanceWalletOutlined,
    Close,
} from '@mui/icons-material';

import { RootState } from '../../../../../../../../../redux/store';
import { closeDrawer } from '../../../../../../../../../redux/reducer/drawerSlice';
import { showSnackbar } from '../../../../../../../../../redux/reducer/snackbarSlice';
import { setModuleRefresh } from '../../../../../../../../../redux/reducer/registration/RefreshSlice';
import { StoreBeneficiaryUtilizationService } from '../../../../../../../../../utils/services/collaboration-agreement/collaboration-agreement.service';

const validationSchema = Yup.object({
    utilization_date: Yup.string().required('Utilization date is required'),
    disbursement_uuid: Yup.string().required('Disbursement is required'),
    amount: Yup.number()
        .typeError('Amount must be a valid number')
        .required('Amount is required')
        .min(1, 'Amount must be greater than 0'),
    utilization_type: Yup.string().required('Utilization type is required'),
    remarks: Yup.string().trim().required('Remarks are required'),
    type: Yup.string().required('Type is required'),
});

const UTILIZATION_TYPES = [
    'medical_expense',
    'medicine',
    'surgery',
    'consultation',
    'other',
];

const TYPES = ['TGS', 'NEFT', 'RTGS', 'cmrf'];

const requiredStar = (
    <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>
        *
    </Box>
);

const fieldLabelSx = {
    mb: 0.75,
    display: 'inline-block',
    fontSize: 13,
    fontWeight: 700,
    color: 'text.primary',
};

const inputSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: '0.2s ease',
        '&:hover': {
            bgcolor: '#fff',
        },
        '&.Mui-focused': {
            bgcolor: '#fff',
        },
        '& .MuiInputAdornment-positionStart': {
            pl: 1.5,
            pr: 0.5,
        },
        '& .MuiInputAdornment-positionEnd': {
            pr: 1.5,
            pl: 0.5,
        },
    },
};

const selectSx = {
    borderRadius: 2,
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-notchedOutline': {
        transition: '0.2s ease',
    },
    '& .MuiInputAdornment-positionStart': {
        pl: 1.5,
        pr: 0.5,
    },
    '& .MuiInputAdornment-positionEnd': {
        pr: 1.5,
        pl: 0.5,
    },
};

const sectionCardSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden',
};

const formatLabel = (value: string) => {
    return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const AddUtilizationDrawer: React.FC = () => {
    const dispatch = useDispatch();

    const { drawerData } = useSelector((state: RootState) => state.drawer);

    const refreshFlag = useSelector(
        (state: RootState) => state.refreshSlice.beneficiary_utilization
    );
    const userDetailsRefreshFlag = useSelector(
        (state: RootState) => state.refreshSlice.user_details
    );

    const [isLoading, setIsLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            utilization_date: '',
            disbursement_uuid: drawerData?.disbursement_uuid || '',
            amount: '',
            utilization_type: 'medical_expense',
            remarks: '',
            type: 'TGS',
            document: null as File | null,
        },
        validationSchema,
        onSubmit: async (values) => {
            try {
                setIsLoading(true);

                const formData = new FormData();

                formData.append('beneficiary_uuid', drawerData?.beneficiary_uuid || '');
                formData.append('tri_party_mou_uuid', drawerData?.tri_party_uuid || '');
                formData.append('disbursement_uuid', values.disbursement_uuid);
                formData.append('hospital_uuid', drawerData?.hospital_uuid || '');
                formData.append(
                    'utilization_date',
                    dayjs(values.utilization_date).format('YYYY-MM-DD')
                );
                formData.append('amount', values.amount.toString());
                formData.append('utilization_type', values.utilization_type);
                formData.append('remarks', values.remarks.trim());
                formData.append('type', values.type);

                if (values.document) {
                    formData.append('document', values.document);
                }

                const { code, message } = await StoreBeneficiaryUtilizationService(formData);

                if (code === 200) {
                    dispatch(
                        showSnackbar({
                            type: 'success',
                            message: message || 'Utilization added successfully',
                        })
                    );

                    dispatch(
                        setModuleRefresh({
                            moduleName: 'beneficiary_utilization',
                            refresh: !refreshFlag,
                        })
                    );
                    dispatch(
                        setModuleRefresh({
                            moduleName: 'user_details',
                            refresh: !userDetailsRefreshFlag,
                        })
                    );

                    dispatch(closeDrawer());
                } else {
                    dispatch(
                        showSnackbar({
                            type: 'error',
                            message: message || 'Something went wrong',
                        })
                    );
                }
            } catch (error) {
                dispatch(
                    showSnackbar({
                        type: 'error',
                        message: 'Something went wrong while saving utilization',
                    })
                );
            } finally {
                setIsLoading(false);
            }
        },
    });

    const selectedFile = formik.values.document as File | null;
    const disbursementList = drawerData?.disbursementList || [];

    return (
        <FormikProvider value={formik}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                    component="form"
                    onSubmit={formik.handleSubmit}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 'calc(100vh - 59px)',
                        maxHeight: 'calc(100vh - 59px)',
                        bgcolor: '#f8fafc',
                    }}
                >


                    {/* Body */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2.5,
                        }}
                    >
                        <Paper sx={sectionCardSx}>
                            <Box
                                sx={{
                                    p: 2,
                                    background:
                                        'linear-gradient(135deg, rgba(25,118,210,0.08), rgba(46,125,50,0.06))',
                                }}
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <MedicalServicesOutlined color="primary" fontSize="small" />
                                    <Typography fontWeight={800}>Utilization Information</Typography>
                                </Stack>
                            </Box>

                            <Divider />

                            <Box sx={{ p: 2 }}>
                                <Grid container spacing={2}>
                                    {/* Utilization Date */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Utilization Date {requiredStar}
                                        </FormLabel>

                                        <DatePicker
                                            value={
                                                formik.values.utilization_date
                                                    ? dayjs(formik.values.utilization_date)
                                                    : null
                                            }
                                            onChange={(val) =>
                                                formik.setFieldValue(
                                                    'utilization_date',
                                                    val ? val.format('YYYY-MM-DD') : ''
                                                )
                                            }
                                            format="DD-MM-YYYY"
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: 'small',
                                                    name: 'utilization_date',
                                                    onBlur: formik.handleBlur,
                                                    placeholder: 'Select date',
                                                    error:
                                                        formik.touched.utilization_date &&
                                                        Boolean(formik.errors.utilization_date),
                                                    helperText:
                                                        formik.touched.utilization_date &&
                                                        formik.errors.utilization_date,
                                                    InputProps: {
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <CalendarMonthOutlined fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    },
                                                    sx: inputSx,
                                                },
                                            }}
                                        />
                                    </Grid>

                                    {/* Disbursement */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Disbursement {requiredStar}
                                        </FormLabel>

                                        <Select
                                            fullWidth
                                            size="small"
                                            name="disbursement_uuid"
                                            value={formik.values.disbursement_uuid}
                                            displayEmpty
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'disbursement_uuid',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.disbursement_uuid &&
                                                Boolean(formik.errors.disbursement_uuid)
                                            }
                                            sx={selectSx}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <ReceiptLongOutlined fontSize="small" />
                                                </InputAdornment>
                                            }
                                        >
                                            <MenuItem value="" disabled>
                                                Select disbursement
                                            </MenuItem>

                                            {disbursementList.map((item: any) => (
                                                <MenuItem key={item.uuid} value={item.uuid}>
                                                    <Stack spacing={0.2}>
                                                        <Typography variant="body2" fontWeight={700}>
                                                            {item.utr_no ||
                                                                item.disbursement_date ||
                                                                item.uuid}
                                                        </Typography>

                                                        {item.amount && (
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                            >
                                                                Amount: ₹{item.amount}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {formik.touched.disbursement_uuid &&
                                            formik.errors.disbursement_uuid &&
                                            typeof formik.errors.disbursement_uuid === 'string' && (
                                                <FormHelperText error>
                                                    {formik.errors.disbursement_uuid}
                                                </FormHelperText>
                                            )}
                                    </Grid>

                                    {/* Amount */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Amount {requiredStar}
                                        </FormLabel>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="amount"
                                            placeholder="Enter amount"
                                            value={formik.values.amount}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (/^\d*$/.test(value)) {
                                                    formik.setFieldValue('amount', value);
                                                }
                                            }}
                                            onBlur={formik.handleBlur}
                                            inputProps={{
                                                inputMode: 'numeric',
                                                style: { textAlign: 'right' },
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CurrencyRupee fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                formik.touched.amount &&
                                                Boolean(formik.errors.amount)
                                            }
                                            helperText={
                                                formik.touched.amount && formik.errors.amount
                                            }
                                            sx={inputSx}
                                        />
                                    </Grid>

                                    {/* Utilization Type */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Utilization Type {requiredStar}
                                        </FormLabel>

                                        <Select
                                            fullWidth
                                            size="small"
                                            name="utilization_type"
                                            value={formik.values.utilization_type}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'utilization_type',
                                                    e.target.value
                                                )
                                            }
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.utilization_type &&
                                                Boolean(formik.errors.utilization_type)
                                            }
                                            sx={selectSx}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <MedicalServicesOutlined fontSize="small" />
                                                </InputAdornment>
                                            }
                                        >
                                            {UTILIZATION_TYPES.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {formatLabel(type)}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {formik.touched.utilization_type &&
                                            formik.errors.utilization_type && (
                                                <FormHelperText error>
                                                    {formik.errors.utilization_type}
                                                </FormHelperText>
                                            )}
                                    </Grid>

                                    {/* Type */}
                                    <Grid item xs={12} sm={6}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Type {requiredStar}
                                        </FormLabel>

                                        <Select
                                            fullWidth
                                            size="small"
                                            name="type"
                                            value={formik.values.type}
                                            onChange={(e) =>
                                                formik.setFieldValue('type', e.target.value)
                                            }
                                            onBlur={formik.handleBlur}
                                            error={
                                                formik.touched.type &&
                                                Boolean(formik.errors.type)
                                            }
                                            sx={selectSx}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <AccountBalanceWalletOutlined fontSize="small" />
                                                </InputAdornment>
                                            }
                                        >
                                            {TYPES.map((type) => (
                                                <MenuItem key={type} value={type}>
                                                    {type.toUpperCase()}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {formik.touched.type && formik.errors.type && (
                                            <FormHelperText error>
                                                {formik.errors.type}
                                            </FormHelperText>
                                        )}
                                    </Grid>

                                    {/* Remarks */}
                                    <Grid item xs={12}>
                                        <FormLabel sx={fieldLabelSx}>
                                            Remarks {requiredStar}
                                        </FormLabel>

                                        <TextField
                                            fullWidth
                                            size="small"
                                            name="remarks"
                                            placeholder="Enter remarks"
                                            multiline
                                            minRows={3}
                                            value={formik.values.remarks}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment
                                                        position="start"
                                                        sx={{ alignSelf: 'flex-start', mt: 1 }}
                                                    >
                                                        <NotesOutlined fontSize="small" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            error={
                                                formik.touched.remarks &&
                                                Boolean(formik.errors.remarks)
                                            }
                                            helperText={
                                                formik.touched.remarks && formik.errors.remarks
                                            }
                                            sx={inputSx}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>

                        {/* Document Upload */}
                        <Paper sx={{ ...sectionCardSx, mt: 2 }}>
                            <Box sx={{ p: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <DescriptionOutlined color="primary" fontSize="small" />

                                    <Box>
                                        <Typography fontWeight={800}>Utilization Document</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Upload bill, receipt, report, utilization proof, or PDF
                                        </Typography>
                                    </Box>
                                </Stack>

                                <Box
                                    component="label"
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        minHeight: 112,
                                        borderRadius: 3,
                                        border: '1.5px dashed',
                                        borderColor: selectedFile ? 'success.main' : 'divider',
                                        bgcolor: selectedFile
                                            ? 'rgba(46,125,50,0.06)'
                                            : 'rgba(15,23,42,0.02)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        transition: '0.2s ease',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: 'rgba(25,118,210,0.05)',
                                        },
                                    }}
                                >
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*,application/pdf"
                                        onChange={(e) =>
                                            formik.setFieldValue(
                                                'document',
                                                e.target.files?.[0] || null
                                            )
                                        }
                                    />

                                    <Stack spacing={1} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2.5,
                                                display: 'grid',
                                                placeItems: 'center',
                                                bgcolor: 'background.paper',
                                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
                                                color: selectedFile
                                                    ? 'success.main'
                                                    : 'primary.main',
                                            }}
                                        >
                                            <CloudUploadOutlined />
                                        </Box>

                                        {selectedFile ? (
                                            <>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{
                                                        maxWidth: 320,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {selectedFile.name}
                                                </Typography>

                                                <Chip
                                                    size="small"
                                                    color="success"
                                                    label={`${Math.ceil(
                                                        selectedFile.size / 1024
                                                    )} KB selected`}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <Typography variant="body2" fontWeight={700}>
                                                    Click to upload document
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Supported formats: Image or PDF
                                                </Typography>
                                            </>
                                        )}
                                    </Stack>
                                </Box>

                                {selectedFile && (
                                    <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        sx={{ mt: 1 }}
                                    >
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                formik.setFieldValue('document', null)
                                            }
                                        >
                                            Remove file
                                        </Button>
                                    </Stack>
                                )}
                            </Box>
                        </Paper>
                    </Box>

                    {/* Footer */}
                    <DialogActions
                        sx={{
                            px: 2.5,
                            py: 1.75,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 2,
                            boxShadow: '0 -8px 24px rgba(15, 23, 42, 0.06)',
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={() => dispatch(closeDrawer())}
                            disabled={isLoading}
                            sx={{
                                borderRadius: 2,
                                px: 2.5,
                                textTransform: 'none',
                                fontWeight: 700,
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            color="success"
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : null
                            }
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                textTransform: 'none',
                                fontWeight: 800,
                                boxShadow: '0 8px 18px rgba(46,125,50,0.25)',
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Save Utilization'}
                        </Button>
                    </DialogActions>
                </Box>
            </LocalizationProvider>
        </FormikProvider>
    );
};

export default AddUtilizationDrawer;